import { AdminLayout } from "./AdminLayout";
import { Plus, Search, MoreVertical, Calendar, CheckCircle, Circle, Clock } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', status: 'todo', priority: 'medium', project_id: '', due_date: ''
  });

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (projectFilter) params.project_id = projectFilter;
      const [tasksRes, projectsRes] = await Promise.all([
        axios.get(`${API}/projects/tasks/all`, { params, withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true })
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [statusFilter, priorityFilter, projectFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.project_id) delete data.project_id;
      if (!data.due_date) delete data.due_date;
      if (editingTask) {
        await axios.put(`${API}/projects/tasks/${editingTask.task_id}`, data, { withCredentials: true });
        toast.success("Task updated");
      } else {
        await axios.post(`${API}/projects/tasks`, data, { withCredentials: true });
        toast.success("Task created");
      }
      setIsDialogOpen(false);
      setEditingTask(null);
      setFormData({ title: '', description: '', status: 'todo', priority: 'medium', project_id: '', due_date: '' });
      fetchData();
    } catch (error) {
      toast.error("Failed to save task");
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title, description: task.description || '', status: task.status,
      priority: task.priority, project_id: task.project_id || '', 
      due_date: task.due_date ? task.due_date.split('T')[0] : ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await axios.delete(`${API}/projects/tasks/${taskId}`, { withCredentials: true });
      toast.success("Task deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const updateStatus = async (taskId, status) => {
    try {
      await axios.put(`${API}/projects/tasks/${taskId}`, { status }, { withCredentials: true });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getProjectName = (projectId) => projects.find(p => p.project_id === projectId)?.name || '';

  const statuses = ['todo', 'in_progress', 'review', 'done'];
  const statusLabels = { todo: 'To Do', in_progress: 'In Progress', review: 'Review', done: 'Done' };
  const statusIcons = { todo: Circle, in_progress: Clock, review: CheckCircle, done: CheckCircle };
  const statusColors = { todo: 'text-[#8A8A8A]', in_progress: 'text-blue-400', review: 'text-yellow-400', done: 'text-green-400' };

  const priorities = ['low', 'medium', 'high', 'urgent'];
  const priorityLabels = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };
  const priorityColors = { low: 'text-[#8A8A8A]', medium: 'text-blue-400', high: 'text-orange-400', urgent: 'text-red-400' };

  // Group by status for Kanban
  const tasksByStatus = statuses.reduce((acc, status) => {
    acc[status] = tasks.filter(t => t.status === status);
    return acc;
  }, {});

  return (
    <AdminLayout title="Tasks">
      <div className="space-y-6" data-testid="admin-tasks">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Task Board</h2>
            <p className="text-sm text-[#8A8A8A]">{tasks.filter(t => t.status !== 'done').length} pending</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2"
                onClick={() => { setEditingTask(null); setFormData({ title: '', description: '', status: 'todo', priority: 'medium', project_id: '', due_date: '' }); }}
                data-testid="add-task-btn">
                <Plus className="w-4 h-4" /> Add Task
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
              <DialogHeader><DialogTitle>{editingTask ? 'Edit Task' : 'Add New Task'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <input type="text" placeholder="Task Title *" required value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})} className="input-field" />
                <textarea placeholder="Description" rows={3} value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})} className="input-field resize-none" />
                <div className="grid grid-cols-2 gap-4">
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="input-field">
                    {statuses.map(status => <option key={status} value={status}>{statusLabels[status]}</option>)}
                  </select>
                  <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="input-field">
                    {priorities.map(priority => <option key={priority} value={priority}>{priorityLabels[priority]}</option>)}
                  </select>
                </div>
                <select value={formData.project_id} onChange={(e) => setFormData({...formData, project_id: e.target.value})} className="input-field">
                  <option value="">No Project</option>
                  {projects.map(project => <option key={project.project_id} value={project.project_id}>{project.name}</option>)}
                </select>
                <input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="input-field" />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">{editingTask ? 'Update' : 'Create'} Task</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field w-48">
            <option value="">All Statuses</option>
            {statuses.map(status => <option key={status} value={status}>{statusLabels[status]}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="input-field w-48">
            <option value="">All Priorities</option>
            {priorities.map(priority => <option key={priority} value={priority}>{priorityLabels[priority]}</option>)}
          </select>
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="input-field w-48">
            <option value="">All Projects</option>
            {projects.map(project => <option key={project.project_id} value={project.project_id}>{project.name}</option>)}
          </select>
        </div>

        {/* Kanban Board */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {statuses.map((status) => {
              const StatusIcon = statusIcons[status];
              return (
                <div key={status} className="w-72 flex-shrink-0">
                  <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StatusIcon className={`w-4 h-4 ${statusColors[status]}`} />
                        <span className="font-medium text-sm">{statusLabels[status]}</span>
                      </div>
                      <span className="text-sm text-[#8A8A8A]">{tasksByStatus[status].length}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {loading ? (
                      [...Array(2)].map((_, i) => (
                        <div key={i} className="card-dashboard animate-pulse">
                          <div className="h-4 bg-[#2E2E2E] rounded w-3/4 mb-2" />
                          <div className="h-3 bg-[#2E2E2E] rounded w-1/2" />
                        </div>
                      ))
                    ) : tasksByStatus[status].length === 0 ? (
                      <div className="text-center py-8 text-[#8A8A8A] text-sm border border-dashed border-[#2E2E2E]">
                        No tasks
                      </div>
                    ) : (
                      tasksByStatus[status].map((task) => (
                        <div key={task.task_id} className="card-dashboard" data-testid={`task-card-${task.task_id}`}>
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-sm">{task.title}</h4>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1 hover:bg-[#2E2E2E] -mr-2 -mt-1"><MoreVertical className="w-4 h-4" /></button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                                <DropdownMenuItem onClick={() => handleEdit(task)} className="cursor-pointer hover:bg-[#2E2E2E]">Edit</DropdownMenuItem>
                                {statuses.filter(s => s !== status).map(s => (
                                  <DropdownMenuItem key={s} onClick={() => updateStatus(task.task_id, s)} className="cursor-pointer hover:bg-[#2E2E2E]">
                                    Move to {statusLabels[s]}
                                  </DropdownMenuItem>
                                ))}
                                <DropdownMenuItem onClick={() => handleDelete(task.task_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className={`${priorityColors[task.priority]}`}>{priorityLabels[task.priority]}</span>
                            {task.due_date && (
                              <span className="text-[#8A8A8A] flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(task.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                          {task.project_id && (
                            <div className="text-xs text-[#8A8A8A] mt-2 truncate">{getProjectName(task.project_id)}</div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Tasks;
